import type { HttpMethod } from './endpoint'

/**
 * The transport seam: one function from a fully built request to a response.
 * `fetchTransport` is the production adapter; `createFakeTransport` in
 * `fake-transport.ts` is the test adapter.
 *
 * A transport resolves for EVERY HTTP status. Turning a non-2xx into an error
 * is the client's job, so both adapters stay equally dumb.
 */
export type Transport = (req: TransportRequest) => Promise<TransportResponse>

export interface TransportRequest {
  method: HttpMethod
  url: string
  headers: Record<string, string>
  /** Already serialised: a JSON string, `FormData` or a `Blob`. */
  body?: BodyInit
  timeoutMs: number
  signal?: AbortSignal
  /** Passed to `fetch` only (Next caching on the server). */
  cache?: RequestCache
  /** Passed to `fetch` only (Next caching on the server). */
  next?: { revalidate?: number | false; tags?: string[] }
}

export interface TransportResponse {
  status: number
  headers: Headers
  /** Parsed JSON; a `Blob` for non-JSON content types; `undefined` for 204 or an empty body. */
  body: unknown
}

/**
 * Thrown by a transport when no response arrived. The client maps it to
 * `ApiError{ kind: 'network' | 'timeout', status: 0 }`; any other error a
 * transport throws is treated as `network`.
 */
export class TransportError extends Error {
  readonly kind: 'network' | 'timeout'

  constructor(kind: 'network' | 'timeout', message: string, options?: { cause?: unknown }) {
    super(message, options)
    this.name = 'TransportError'
    this.kind = kind
  }
}

async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined

  const contentType = res.headers.get('content-type') ?? ''
  if (contentType.includes('json')) {
    const text = await res.text()
    if (!text) return undefined
    try {
      return JSON.parse(text)
    } catch {
      // A proxy can label an HTML error page as JSON. Keep the text for the error message.
      return text
    }
  }

  const blob = await res.blob()
  return blob.size === 0 ? undefined : blob
}

/**
 * The production transport, on `fetch`.
 *
 * Timeout and caller abort share one local `AbortController`. It deliberately
 * does not use `AbortSignal.any`: that needs Safari 17.4 / Chrome 116, and Next's
 * default browserslist still ships to Safari 16.4 with no polyfill, so it would
 * throw before every request on older school iPads.
 *
 * A caller abort rejects with the caller's abort reason (TanStack Query cancels
 * that way and ignores the rejection), not with a `TransportError`.
 */
export const fetchTransport: Transport = async req => {
  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort(new TransportError('timeout', `Request timed out after ${req.timeoutMs}ms`))
  }, req.timeoutMs)

  const callerSignal = req.signal
  const forwardCallerAbort = () => controller.abort(callerSignal?.reason)
  if (callerSignal?.aborted) forwardCallerAbort()
  else callerSignal?.addEventListener('abort', forwardCallerAbort, { once: true })

  try {
    const init: RequestInit = { method: req.method, headers: req.headers, body: req.body, signal: controller.signal }
    if (req.cache) init.cache = req.cache
    if (req.next) init.next = req.next

    const res = await fetch(req.url, init)
    return { status: res.status, headers: res.headers, body: await readBody(res) }
  } catch (error) {
    if (timedOut) {
      throw new TransportError('timeout', `Request timed out after ${req.timeoutMs}ms`, { cause: error })
    }
    if (callerSignal?.aborted) throw callerSignal.reason ?? error
    throw new TransportError('network', error instanceof Error ? error.message : 'Network error', { cause: error })
  } finally {
    clearTimeout(timer)
    callerSignal?.removeEventListener('abort', forwardCallerAbort)
  }
}
